import api, { request } from '../../../api';
import { IErrorsStore } from '../../errors/store';
import { IActionGroup, IStore } from '../../index';
import { IAdminEventEnrollmentsStore, IEventEnrollment } from './store';

export interface IGuest {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    profile: {
      ghinNumber?: number;
      birthDate?: string;
    };
  };
  entryFee: number;
  flight: number;
  handicap: number;
}

export interface IAdminEventEnrollmentsActions extends IActionGroup {
  get: (
    clubId: number,
    eventId: number
  ) => { eventEnrollments?: IAdminEventEnrollmentsStore; errors?: IErrorsStore };
  edit: (
    clubId: number,
    eventId: number,
    items: Partial<IEventEnrollment> | Array<Partial<IEventEnrollment>>
  ) => { eventEnrollments?: IAdminEventEnrollmentsStore; errors?: IErrorsStore };
  sendMembersInvitations: (
    eventId: number,
    userIds: number[]
  ) => { eventEnrollments?: IAdminEventEnrollmentsStore; errors?: IErrorsStore };
  addGuest: (
    clubId: number,
    eventId: number,
    roundId: number,
    guest: IGuest
  ) => { eventEnrollments?: IAdminEventEnrollmentsStore; errors?: IErrorsStore };
}

const actions: IActionGroup = {
  sendMembersInvitations: async (state: IStore, eventId: number, userIds: number[]) => {
    const response = await request.post(`${api.admin.invitations}users/`, {
      event: eventId,
      users: userIds
    });

    if (response.error) {
      return { errors: { invitations: { msg: 'Error inviting members to the event.' } } };
    }

    const oldEnrollments = state.adminEventEnrollments[eventId] || [];

    // TODO: handle response.invalid invitations
    const enrollments: IEventEnrollment[] = oldEnrollments
      .concat(response.valid)
      .filter((obj, pos, arr) => arr.map(mapObj => mapObj.id).indexOf(obj.id) === pos);

    return {
      adminEventEnrollments: {
        ...state.adminEventEnrollments,
        [eventId]: enrollments
      }
    };
  },

  get: async (state: IStore, clubId: number, eventId: number) => {
    if (state.adminEventEnrollments[eventId]) {
      return {
        adminEventEnrollments: state.adminEventEnrollments
      };
    }

    const response = await request.get(api.admin.eventEnrollments(clubId, eventId));

    if (response.error) {
      return {
        errors: { eventEnrollments: { msg: 'Error retrieving the Event Enrollment List.' } }
      };
    }

    const enrollments: IEventEnrollment[] = response;

    return {
      adminEventEnrollments: {
        ...state.adminEventEnrollments,
        [eventId]: enrollments
      }
    };
  },

  edit: async (
    state: IStore,
    clubId: number,
    eventId: number,
    items: Partial<IEventEnrollment> | Array<Partial<IEventEnrollment>>
  ) => {
    const enrollment = items as Partial<IEventEnrollment>;
    const enrollmentArray = items as Array<Partial<IEventEnrollment>>;
    let body;

    if (!enrollmentArray.length) {
      const { flight, user, ...e1 } = enrollment;
      if (flight) {
        (e1 as any).flight = flight.id;
      }
      if (user) {
        (e1 as any).user = user.id;
      }
      body = e1;
    } else {
      body = {
        instances: enrollmentArray.map(e => {
          const { flight, user, ...e1 } = e;
          if (flight) {
            (e1 as any).flight = flight.id;
          }
          if (user) {
            (e1 as any).user = user.id;
          }
          return e1;
        })
      };
    }

    const response = await request.patch(
      api.admin.eventEnrollments(clubId, eventId) +
        (enrollmentArray.length ? '' : `${enrollment.id}/`),
      body
    );

    if (response.error) {
      return {
        errors: { eventEnrollments: { msg: 'Error Updating Enrollment.' } }
      };
    }

    const enrollments = !enrollmentArray.length
      ? state.adminEventEnrollments[eventId].filter(e => e.id !== enrollment.id).concat([response])
      : state.adminEventEnrollments[eventId]
          .filter(e => !enrollmentArray.some(i => i.id === e.id))
          .concat(response);

    return {
      adminEventEnrollments: {
        ...state.adminEventEnrollments,
        [eventId]: enrollments
      }
    };
  },

  addGuest: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    guest: IGuest
  ) => {
    const response = await request.post(`${api.admin.eventEnrollments(clubId, eventId)}new_user/`, {
      ...guest,
      user: {
        ...guest.user,
        username: guest.user.email
      }
    });

    if (response.error) {
      return { errors: { invitations: { msg: 'Error creating new member.' } } };
    }

    const enrollments = state.adminEventEnrollments[eventId] || [];

    const eventRounds = state.adminEventRounds;
    const eventRound = eventRounds[roundId];

    // HACK TO ADD THE GUEST TO THE FIRST ROUND OF THE EVENT
    // TODO: check if the guest has to be added to another rounds
    const adminEventRounds = {
      ...eventRounds,
      [roundId]: {
        ...eventRound,
        enrollments: [
          ...(eventRound && eventRound.enrollments ? eventRound.enrollments : []),
          response
        ]
      }
    };

    return {
      adminEventEnrollments: {
        ...state.adminEventEnrollments,
        [eventId]: [...enrollments, response]
      },
      adminEventRounds
    };
  }
};

export default actions;
